import { BillingPlanDto, REMINDERS_PAUSED_REASONS, resolveRemindersPausedReason } from '@shared/core/billing'
import { useMemo } from 'react'
import { billingTypes } from 'src/modules/payment/constants/payment.constants'
import { useSubscription } from 'src/modules/payment/hooks/use-subscription'
import { useSubscriptionPlans } from 'src/modules/payment/hooks/use-subscription-plans'
import type { UsePlanDataOptions } from 'src/modules/payment/types/payment.types'
import { isPlanNotForSale, isShopifyBilledProfile } from 'src/modules/payment/utils/helpers'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import { useFreePlan } from './use-free-plan'

export function usePlanData(options: UsePlanDataOptions = {}) {
  const profile = useProfile({}, options.profileOptions)
  const subscription = useSubscription({}, options.subscriptionOptions)
  const subscriptionPlans = useSubscriptionPlans({ type: options.type }, options.subscriptionPlansOptions)
  const freePlan = useFreePlan(undefined, options.freePlanOptions)
  const shopifyBillablePlansOnly = isShopifyBilledProfile(profile.data)

  const loading = useMemo(
    () => subscription.isLoading || subscriptionPlans.isLoading || freePlan.isLoading || profile.isLoading,
    [freePlan.isLoading, profile.isLoading, subscription.isLoading, subscriptionPlans.isLoading],
  )

  const pausedReason = profile.data ? resolveRemindersPausedReason(profile.data) : null
  const currentPlan = useMemo<BillingPlanDto | null>(() => {
    if (pausedReason === REMINDERS_PAUSED_REASONS.subscriptionEnded) return freePlan.data
    return subscriptionPlans.data?.find((p) => p.paddle_id === subscription.data?.plan_id) ?? freePlan.data
  }, [freePlan.data, pausedReason, subscription.data?.plan_id, subscriptionPlans.data])

  const filteredPlans = useMemo(() => {
    if (Array.isArray(subscriptionPlans.data)) {
      return (
        subscriptionPlans.data
          // filter plans depending on current plan
          .filter((plan) => (isPlanNotForSale(currentPlan) ? plan.paddle_id !== currentPlan?.paddle_id : plan))
          .filter((plan) => !shopifyBillablePlansOnly || !!plan.shopify_plan_key)
          // sort by price ascending
          .sort((a, b) => a.price - b.price)
      )
    }

    return []
  }, [currentPlan, shopifyBillablePlansOnly, subscriptionPlans.data])

  const availablePlans = useMemo(() => {
    if (options.isYearly === undefined) return []

    return filteredPlans?.filter((plan) =>
      options.isYearly
        ? plan.billing_type === billingTypes.longInterval
        : plan.billing_type === billingTypes.shortInterval,
    )
  }, [filteredPlans, options.isYearly])

  return {
    loading,
    currentPlan,
    filteredPlans,
    availablePlans,
    subscriptionPlans,
  }
}
